import { useTranslation } from 'react-i18next';
import {
    mdiAccount,
    mdiAccountGroup,
    mdiBookshelf,
    mdiPlus,
    mdiSchool,
    mdiSeat,
    mdiTransitConnectionVariant,
} from '@mdi/js';
import { Box, MenuItem, Stack, SxProps } from '@mui/material';

import { Icon } from 'common/components/Icon';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { Link } from 'common/components/Link';
import { Divider } from 'common/components/Divider';
import { Button } from 'common/components/Button';
import { IOfferEventType } from 'graphql/types';
import { EFilterType } from 'offer/constants/offer';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { useAppPermissions } from 'user/hooks/usePermissions';

const menuItemStyle: SxProps = {
    pr: 3,
};

export const NavigationCreateMaterial = () => {
    const [translate] = useTranslation();

    const {
        canCreateCourse = false,
        canCreateTraining = false,
        canCreateMeeting = false,
        canCreateOffer = false,
        canCreateUser = false,
        canCreateGroup = false,
    } = useAppPermissions('general', 'management');

    const { openGlobalDrawer } = useGlobalDrawer();

    if (
        !canCreateCourse &&
        !canCreateTraining &&
        !canCreateMeeting &&
        !canCreateOffer &&
        !canCreateUser &&
        !canCreateGroup
    ) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                mr: '5px',
                alignItems: 'center',
            }}
        >
            <DropdownMenu
                anchor={
                    <Button
                        aria-label={translate('newMaterialButton')}
                        color="primary"
                        startIcon={<Icon path={mdiPlus} size="1.9rem" />}
                        sx={{
                            height: '37px',
                            borderRadius: 5,
                            pl: 2.5,
                            ml: 0.5,

                            '.MuiButton-startIcon': {
                                mr: 1,
                            },
                        }}
                        variant="contained"
                    >
                        {translate('newMaterialButton')}
                    </Button>
                }
                placement="bottom"
            >
                <Stack
                    divider={<Divider sx={{ my: 1.25 }} />}
                    role="menu"
                    tabIndex={-1}
                >
                    {(canCreateCourse || canCreateTraining) && (
                        <>
                            {canCreateCourse && (
                                <MenuItem
                                    component={Link}
                                    sx={menuItemStyle}
                                    tabIndex={0}
                                    to="COURSE_CREATE"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mr: 1,
                                            alignContent: 'center',
                                        }}
                                    >
                                        <Icon path={mdiSchool} size="2.1rem" />
                                    </Box>

                                    {translate('course')}
                                </MenuItem>
                            )}

                            {canCreateTraining && (
                                <MenuItem
                                    component={Link}
                                    sx={menuItemStyle}
                                    tabIndex={0}
                                    to="TRAINING_CREATE"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mr: 1,
                                            alignContent: 'center',
                                        }}
                                    >
                                        <Icon
                                            path={mdiTransitConnectionVariant}
                                            size="2.1rem"
                                        />
                                    </Box>

                                    {translate('training')}
                                </MenuItem>
                            )}
                        </>
                    )}

                    {(canCreateMeeting || canCreateOffer) && (
                        <>
                            {canCreateMeeting && (
                                <MenuItem
                                    component={Link}
                                    query={`?type=${EFilterType.meetingWebinar}`}
                                    state={{
                                        offerEventType: IOfferEventType.Meeting,
                                    }}
                                    sx={menuItemStyle}
                                    tabIndex={0}
                                    to="OFFER_MEETING_CREATE"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mr: 1,
                                            alignContent: 'center',
                                        }}
                                    >
                                        <Icon path={mdiSeat} size="2.1rem" />
                                    </Box>

                                    {translate('meeting')}
                                </MenuItem>
                            )}

                            {canCreateOffer && (
                                <MenuItem
                                    component={Link}
                                    query={`?type=${EFilterType.training}`}
                                    state={{
                                        offerType: IOfferEventType.Training,
                                    }}
                                    sx={menuItemStyle}
                                    tabIndex={0}
                                    to="OFFER_CREATE"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mr: 1,
                                            alignContent: 'center',
                                        }}
                                    >
                                        <Icon
                                            path={mdiBookshelf}
                                            size="2.1rem"
                                        />
                                    </Box>

                                    {translate('offers')}
                                </MenuItem>
                            )}
                        </>
                    )}

                    {(canCreateUser || canCreateGroup) && (
                        <>
                            {canCreateUser && (
                                <MenuItem
                                    component={Link}
                                    sx={menuItemStyle}
                                    tabIndex={0}
                                    onClick={() => {
                                        openGlobalDrawer({
                                            type: IGlobalDrawerType.UserCreate,
                                        });
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mr: 1,
                                            alignContent: 'center',
                                        }}
                                    >
                                        <Icon path={mdiAccount} size="2.1rem" />
                                    </Box>

                                    {translate('user')}
                                </MenuItem>
                            )}

                            {canCreateGroup && (
                                <MenuItem
                                    component={Link}
                                    sx={menuItemStyle}
                                    tabIndex={0}
                                    to="GROUP_CREATE"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mr: 1,
                                            alignContent: 'center',
                                        }}
                                    >
                                        <Icon
                                            path={mdiAccountGroup}
                                            size="2.1rem"
                                        />
                                    </Box>

                                    {translate('group')}
                                </MenuItem>
                            )}
                        </>
                    )}
                </Stack>
            </DropdownMenu>
        </Box>
    );
};
